// Caricamento di un dato dall'API con i tre stati che ogni schermata deve saper mostrare:
// sto caricando, è andata male, ecco il dato.
//
// È volutamente minimo: nessuna cache, nessuno stato globale. Ogni schermata chiede quello
// che le serve quando compare e lo ricarica quando cambiano i suoi parametri; `reload` esiste
// perché dopo un'azione (riprogrammo, allego, approvo) il dato a video va riletto dal server,
// che è l'unico a sapere com'è finita davvero.

import { useEffect, useRef, useState, type DependencyList } from 'react'

export interface Async<T> {
  data?: T
  loading: boolean
  error?: unknown
  /** Rilegge il dato dal server. */
  reload: () => void
}

/**
 * @param enabled Finché è falso non si chiede niente e si resta in caricamento. Serve a chi
 *   dipende da un dato che arriva prima (l'elenco dei punti vendita): partire comunque
 *   significherebbe mandare una richiesta costruita su informazioni incomplete.
 */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: DependencyList,
  enabled = true,
): Async<T> {
  const [state, setState] = useState<{ data?: T; loading: boolean; error?: unknown }>({ loading: true })
  const [nonce, setNonce] = useState(0)

  // Il caricatore cambia a ogni render (è una funzione anonima): teniamo l'ultimo, ma facciamo
  // partire la richiesta solo quando cambiano le dipendenze dichiarate. Così non si scatena un
  // ciclo di richieste e la lista delle dipendenze resta l'unica cosa da leggere per capire
  // quando la schermata torna al server.
  const latest = useRef(loader)
  latest.current = loader

  useEffect(() => {
    let alive = true
    setState({ loading: true })
    if (!enabled) return

    latest
      .current()
      .then((data) => {
        if (alive) setState({ data, loading: false })
      })
      .catch((error: unknown) => {
        if (alive) setState({ loading: false, error })
      })

    return () => {
      // La risposta di una richiesta superata non deve sovrascrivere quella corrente.
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled])

  return { ...state, reload: () => setNonce((n) => n + 1) }
}
